<template>
  <div class="llm-textarea-with-insert">
    <Input
      ref="innerInput"
      class="llm-textarea-with-insert__input"
      type="textarea"
      :value="value"
      :rows="rows"
      :placeholder="placeholder"
      :maxlength="maxlength"
      :show-word-limit="false"
      :element-id="elementId"
      :name="name"
      v-bind="$attrs"
      v-on="inputListeners"
    />
    <div v-if="showInsert" class="llm-textarea-with-insert__footer">
      <LlmInsertMemoryDropdown
        v-if="memoryEnabled"
        button-style="footer"
        placement="top-end"
        @insert="onInsertMemory"
      />
      <LlmInsertPauseDropdown
        button-style="footer"
        placement="top-end"
        @insert="onInsertPause"
      />
      <LlmInsertParamDropdown
        :customer-info-params="customerInfoParams"
        button-style="footer"
        placement="top-end"
        @insert="onInsertParam"
      />
      <span v-if="maxlength != null" class="llm-textarea-with-insert__count">{{ textLength }}/{{ maxlength }}</span>
    </div>
  </div>
</template>

<script>
import InsertParamDropdown from './InsertParamDropdown.vue'
import InsertMemoryDropdown from './InsertMemoryDropdown.vue'
import InsertPauseDropdown from './InsertPauseDropdown.vue'
import {
  buildInlineInsertText,
  buildInsertParamText,
  focusTextareaAt,
} from '../../utils/textareaInsertHelpers'

export default {
  name: 'LlmTextareaWithInsertParam',
  components: {
    LlmInsertParamDropdown: InsertParamDropdown,
    LlmInsertMemoryDropdown: InsertMemoryDropdown,
    LlmInsertPauseDropdown: InsertPauseDropdown,
  },
  inheritAttrs: false,
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    rows: {
      type: Number,
      default: 8,
    },
    placeholder: {
      type: String,
      default: '',
    },
    maxlength: {
      type: Number,
      default: null,
    },
    customerInfoParams: {
      type: Array,
      default: () => [],
    },
    memoryEnabled: {
      type: Boolean,
      default: false,
    },
    showInsert: {
      type: Boolean,
      default: true,
    },
    elementId: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
  },
  computed: {
    textLength() {
      return String(this.value != null ? this.value : '').length
    },
    inputListeners() {
      const listeners = { ...this.$listeners }
      delete listeners.insert
      return listeners
    },
  },
  methods: {
    getTextareaEl() {
      const comp = this.$refs.innerInput
      if (!comp) return null
      return (
        (comp.$refs && comp.$refs.textarea) ||
        (comp.$el && comp.$el.querySelector && comp.$el.querySelector('textarea'))
      )
    },
    insertToken(token, { inline = false } = {}) {
      if (token == null || token === '') return
      const ta = this.getTextareaEl()
      const v0 = this.value != null ? String(this.value) : ''
      const start = ta && typeof ta.selectionStart === 'number' ? ta.selectionStart : v0.length
      const end = ta && typeof ta.selectionEnd === 'number' ? ta.selectionEnd : v0.length
      const insertText = inline
        ? buildInlineInsertText(token)
        : buildInsertParamText(v0, start, token)
      let next = v0.slice(0, start) + insertText + v0.slice(end)
      if (this.maxlength != null && next.length > this.maxlength) {
        next = next.slice(0, this.maxlength)
      }
      this.$emit('input', next)
      if (!ta) return
      this.$nextTick(() => {
        focusTextareaAt(ta, start + insertText.length, next.length)
      })
    },
    onInsertParam(token) {
      this.insertToken(token, { inline: false })
    },
    onInsertMemory(token) {
      this.insertToken(token, { inline: true })
    },
    onInsertPause(token) {
      this.insertToken(token, { inline: true })
    },
  },
}
</script>

<style lang="scss" scoped>
.llm-textarea-with-insert {
  border: 1px solid #dcdee2;
  border-radius: 4px;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus-within {
    border-color: #5668f0;
    box-shadow: 0 0 0 2px rgba(86, 104, 240, 0.12);
  }

  &__input {
    ::v-deep .ivu-input {
      border: none !important;
      box-shadow: none !important;
      border-radius: 0;
      resize: vertical;
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 4px 12px 8px;
    background: #fff;
  }

  &__count {
    font-size: 12px;
    color: #808695;
    line-height: 1;
    user-select: none;
    flex-shrink: 0;
  }
}
</style>
